// `Avatar` — initials-based identity primitive. V1 renders the computed
// initials of a display name inside a circular swatch; later iterations
// can add an image fallback or color-by-name variant without changing
// the API surface. Used by the Live In Club row cards to lend each
// Talent a visual anchor without storing a photo (per PRINCIPLES.md
// §Talent Privacy — no profile imagery in V1).
//
// Initial-derivation rules:
//   - Single token (e.g. "Aria")  → first letter ("A").
//   - Multi-token (e.g. "Aria Nova", "Aria de la Vega") → first letter
//     of the first token + first letter of the *last* token ("AV").
//   - Leading/trailing whitespace is trimmed; collapsed whitespace is
//     treated as a single separator.
//   - Empty / whitespace-only name → empty string; the avatar still
//     renders as a circular shape so layout stays stable.

import { type HTMLAttributes, useId } from 'react';

import { classlist } from '~/_shared/design-system/lib/classlist';

import './avatar.css';

type AvatarSize = 'sm' | 'md' | 'lg';

interface AvatarProps
	extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
	display_name: string;
	size?: AvatarSize;
}

const deriveInitials = (display_name: string): string => {
	const tokens = display_name.trim().split(/\s+/).filter(Boolean);
	if (tokens.length === 0) return '';
	// The `?? ''` fallbacks below are TS narrowing under
	// noUncheckedIndexedAccess only — the guard above proves both
	// indices are populated. v8 sees them as branches; ignore.
	/* v8 ignore next */
	const first = tokens[0] ?? '';
	if (tokens.length === 1) return first.charAt(0).toUpperCase();
	/* v8 ignore next */
	const last = tokens[tokens.length - 1] ?? '';
	return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
};

const Avatar = ({
	display_name,
	size = 'md',
	id,
	className,
	'aria-label': aria_label,
	...rest
}: AvatarProps) => {
	const initials = deriveInitials(display_name);
	const resolved_label = aria_label ?? display_name;
	// Instance-unique default id (explicit `id` prop wins) — see BaseButton.
	const auto_id = useId();
	return (
		<span
			{...rest}
			id={id ?? `Avatar-${auto_id}`}
			role='img'
			aria-label={resolved_label}
			className={classlist('avatar', `avatar--${size}`, className)}
		>
			<span aria-hidden='true' className='avatar__initials'>
				{initials}
			</span>
		</span>
	);
};

export type { AvatarProps, AvatarSize };
export { Avatar, deriveInitials };
